import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, type PageMeta } from '@/lib/api';
import type {
  OnlinePaymentReview,
  OnlinePaymentsQuery,
  ResolveOnlinePaymentInput,
} from './types';

export function useOnlinePayments(query: OnlinePaymentsQuery) {
  return useQuery({
    queryKey: ['online-payments', query],
    queryFn: ({ signal }) =>
      api.get<OnlinePaymentReview[], PageMeta>('/billing/online-payments', {
        signal,
        query: { ...query, pageSize: query.pageSize ?? 25 },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useResolveOnlinePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      resolution,
      note,
      refundRef,
    }: ResolveOnlinePaymentInput) =>
      api
        .post<OnlinePaymentReview>(
          `/billing/online-payments/${paymentId}/resolve`,
          {
            resolution,
            note,
            ...(resolution === 'refunded' ? { refundRef } : {}),
          },
        )
        .then((response) => response.data),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['online-payments'] });
      void queryClient.invalidateQueries({
        queryKey: ['bill', payment.billId],
      });
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
    },
  });
}
